import { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "../ui/command";
import { Check, CaretUpDown } from "@phosphor-icons/react";

const chiuso = (c) => c.stato === "chiuso";
const byNome = (a, b) => (a.nome || "").localeCompare(b.nome || "", "it", { sensitivity: "base" });

/** Cantieri attivi in ordine alfabetico, poi i chiusi (più chiari). */
export const ordinaCantieri = (cantieri = []) => ({
  attivi: cantieri.filter((c) => !chiuso(c)).sort(byNome),
  chiusi: cantieri.filter(chiuso).sort(byNome),
});

/** Selettore cantiere con ricerca: prima gli attivi (A→Z), poi i chiusi in grigio. */
export function CantierePicker({ cantieri = [], value = "", onChange, placeholder = "+ scegli cantiere", vuotoLabel = "— Nessuno —", className = "", size = "sm", disabled = false, testId = "cantiere-picker", mostraCommittente = false }) {
  const [open, setOpen] = useState(false);
  const { attivi, chiusi } = useMemo(() => ordinaCantieri(cantieri), [cantieri]);
  const sel = cantieri.find((c) => c.id === value);
  const h = size === "xs" ? "h-7 text-xs" : "h-9 text-sm";
  const label = (c) => `${c.nome}${mostraCommittente && c.committente ? ` · ${c.committente}` : ""}`;
  const scegli = (id) => { onChange(id); setOpen(false); };
  const Item = ({ c, muted }) => (
    <CommandItem value={`${c.nome} ${c.committente || ""}`} onSelect={() => scegli(c.id)} className={`text-xs ${muted ? "text-slate-400" : ""}`} data-testid={`${testId}-opt-${c.id}`}>
      <Check size={12} className={`mr-1.5 shrink-0 ${value === c.id ? "opacity-100" : "opacity-0"}`} />
      <span className="truncate">{label(c)}</span>
      {muted && <span className="ml-auto text-[9px] font-mono uppercase">chiuso</span>}
    </CommandItem>
  );
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" disabled={disabled} className={`${h} w-full inline-flex items-center justify-between gap-1 rounded-md border px-2 text-left truncate bg-white ${sel ? (chiuso(sel) ? "border-slate-200 text-slate-400" : "border-slate-300 text-slate-800 font-medium") : "border-dashed border-blue-300 text-blue-700 italic"} ${className}`} data-testid={testId} title={sel ? label(sel) : placeholder}>
          <span className="truncate">{sel ? label(sel) : placeholder}</span>
          <CaretUpDown size={12} className="shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[280px]" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
        <Command loop>
          <CommandInput placeholder="Cerca cantiere…" className="h-8 text-xs" autoFocus data-testid={`${testId}-search`} />
          <CommandList className="max-h-64">
            <CommandEmpty className="py-3 text-xs text-slate-500 text-center">Nessun cantiere trovato</CommandEmpty>
            {vuotoLabel && (
              <CommandGroup>
                <CommandItem value="__nessuno__" onSelect={() => scegli("")} className="text-xs text-slate-500" data-testid={`${testId}-opt-nessuno`}>
                  <Check size={12} className={`mr-1.5 ${!value ? "opacity-100" : "opacity-0"}`} />{vuotoLabel}
                </CommandItem>
              </CommandGroup>
            )}
            {attivi.length > 0 && <CommandGroup heading="Attivi">{attivi.map((c) => <Item key={c.id} c={c} />)}</CommandGroup>}
            {attivi.length > 0 && chiusi.length > 0 && <CommandSeparator />}
            {chiusi.length > 0 && <CommandGroup heading="Chiusi">{chiusi.map((c) => <Item key={c.id} c={c} muted />)}</CommandGroup>}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
